import { useState, useEffect, useCallback } from "react";
import { useBranches } from "@/hooks/useBranches";
import { useBrandsApi } from "@/hooks/useBrandsApi";
import { useUsersApi } from "@/hooks/useUsersApi";
import { useAuth } from "@/hooks/useAuth";

export function useOrderFilters() {
  const { branches } = useBranches();
  const { brands, fetchAllBrands } = useBrandsApi();
  const { getUserByEmail } = useUsersApi();
  const { user, isManager } = useAuth();

  const [searchQuery, setSearchQuery] = useState("");
  const [selectedBranches, setSelectedBranches] = useState<(number | string)[]>(
    [],
  );
  const [selectedBrands, setSelectedBrands] = useState<(number | string)[]>([]);
  const [selectedStatus, setSelectedStatus] = useState<string>("all");
  const [isAdmin, setIsAdmin] = useState(false);
  const [userBrands, setUserBrands] = useState<
    Array<{ id: number; name: string }>
  >([]);
  const [isLoadingUser, setIsLoadingUser] = useState(true);

  useEffect(() => {
    const checkUserType = async () => {
      if (user?.email) {
        try {
          const userData = await getUserByEmail(user.email);

          const isUserAdmin = Boolean(userData?.userType === "admin");
          setIsAdmin(isUserAdmin);

          // Si el usuario no es admin, obtener sus marcas asignadas
          if (!isUserAdmin && userData?.roleSpecificData?.brands) {
            setUserBrands(userData.roleSpecificData.brands);
          } else if (isUserAdmin) {
            setUserBrands([]);
          }
        } catch (error) {
          console.error("❌ Error checking user type:", error);
          setIsAdmin(false);
          setUserBrands([]);
        } finally {
          setIsLoadingUser(false);
        }
      } else {
        setIsLoadingUser(false);
      }
    };

    checkUserType();
    // Avoid refetching brands if already loaded
    if (brands.length === 0) {
      fetchAllBrands(isManager ? user : null);
    }
  }, [user?.email, getUserByEmail, brands.length]);

  // Calcular las sucursales disponibles según las marcas accesibles
  const getAvailableBranches = () => {
    if (isAdmin) {
      return branches;
    } else {
      const accessibleBrandIds = userBrands.map((brand) => brand.id);
      return branches.filter((branch) =>
        accessibleBrandIds.includes(branch.brand_id),
      );
    }
  };

  const availableBranches = getAvailableBranches();

  // Función para filtrar y ordenar órdenes basada en todos los criterios
  const filterOrders = useCallback(
    (orders: any[]) => {
      const filtered = orders.filter((order) => {
        const searchId = Number(searchQuery);
        const matchesSearch =
          !searchQuery ||
          (!isNaN(searchId) && order.id === searchId) ||
          order.tracking_id.toLowerCase().includes(searchQuery.toLowerCase());

        let matchesBranch = true;
        if (selectedBranches.length > 0) {
          const selectedBranchIds = selectedBranches.map((id) =>
            typeof id === "number" ? id : parseInt(id.toString()),
          );
          matchesBranch = selectedBranchIds.includes(order.branch_id);
        }

        let matchesBrand = true;
        if (selectedBrands.length > 0) {
          const branch = branches.find((b) => b.id === order.branch_id);
          matchesBrand =
            branch && selectedBrands.includes(branch.brand_id) ? true : false;
        }

        const matchesStatus =
          selectedStatus === "all" || order.status === selectedStatus;

        // Filtro adicional: solo mostrar órdenes de sucursales accesibles para usuarios no admin
        // Si aún no cargan brands/branches del usuario, no filtrar (el backend ya aplica permisos)
        let matchesUserPermissions = true;
        if (!isAdmin) {
          if (userBrands.length === 0 || branches.length === 0) {
            matchesUserPermissions = true;
          } else {
            const orderBranch = branches.find((b) => b.id === order.branch_id);
            const accessibleBrandIds = userBrands.map((brand) => brand.id);
            matchesUserPermissions = orderBranch
              ? accessibleBrandIds.includes(orderBranch.brand_id)
              : false;
          }
        }

        return (
          matchesSearch &&
          matchesBranch &&
          matchesBrand &&
          matchesStatus &&
          matchesUserPermissions
        );
      });

      // Ordenar por prioridad (mayor número primero), luego por fecha de creación (más reciente primero)
      return filtered.sort((a, b) => {
        // Primero, ordenar por prioridad (numérica, descendente - números mayores primero)
        const aPriority = Number(a.priority) || 0;
        const bPriority = Number(b.priority) || 0;

        if (aPriority !== bPriority) {
          return bPriority - aPriority; // Mayor prioridad primero
        }

        // Si la prioridad es igual, ordenar por fecha de creación (más reciente primero)
        const aDate = new Date(a.created_at).getTime();
        const bDate = new Date(b.created_at).getTime();

        return bDate - aDate; // Más reciente primero
      });
    },
    [
      searchQuery,
      selectedBranches,
      selectedBrands,
      selectedStatus,
      isAdmin,
      userBrands,
      branches,
    ],
  );

  const brandOptions = brands.map((brand) => ({
    value: brand.id,
    label: brand.name,
  }));

  return {
    // Estados
    searchQuery,
    selectedBranches,
    selectedBrands,
    selectedStatus,
    isLoadingUser,

    // Datos procesados
    availableBranches,
    brandOptions,

    // Funciones
    setSearchQuery,
    setSelectedBranches,
    setSelectedBrands,
    setSelectedStatus,
    filterOrders,
  };
}
